// src/myPlans/PlanPurchasePage.js
// Plans modal. Free users start a Stripe checkout; Pro subscribers change their minutes or billing
// period (confirmed in PlanConfirmation); Pro accounts without a Stripe subscription (custom plans
// set up by the team) only see what they have.

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { loadStripe } from '@stripe/stripe-js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { auth, db } from '../firebase';
import { track } from '../analytics/track';
import PlanConfirmation from './PlanConfirmation';
import { MINUTE_OPTIONS, monthlyPrice } from './planPricing';
import { PRO_MESSAGE_LIMIT } from '../myWorkspace/workspaceComponents/chat/chatModel';
import './planPurchasePage.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUB_KEY);

const FEATURES = [
  'Everything in Free',
  'Upload your own videos',
  `${PRO_MESSAGE_LIMIT} messages per video`,
  '16 GB file size',
  '3h 30m video length',
  'Bumpups Creator Studio',
  'Publish and schedule to YouTube',
  'Description templates',
  'API access for YouTube links',
];

// source: where the modal was opened from, for the funnel events (e.g. 'upload_button', 'chat_limit')
export default function PlanPurchasePage({ onClose, source = 'other' }) {
  const user = auth.currentUser;
  const [plan, setPlan] = useState(null);
  // Monthly first: since 2025, 57% of people whose first Stripe page showed the $192 annual
  // charge left without buying, against 27% of those who started on monthly
  const [period, setPeriod] = useState('monthly');
  const [minutes, setMinutes] = useState(MINUTE_OPTIONS[0]);
  const [checkout, setCheckout] = useState('idle'); // idle | loading | error
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => { track('plans_opened', { source }); }, [source]);

  // Prevent background scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Escape closes the modal — not while the confirmation step is up (it has its own buttons,
  // disabled while a plan change is being sent)
  useEffect(() => {
    if (showConfirmation) return undefined;
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showConfirmation, onClose]);

  // A click on the dimmed backdrop closes the modal — only when the press started there too,
  // so selecting text inside and releasing outside doesn't
  const pressedBackdrop = useRef(false);
  const backdropProps = {
    onMouseDown: e => { pressedBackdrop.current = e.target === e.currentTarget; },
    onClick: e => { if (pressedBackdrop.current && e.target === e.currentTarget) onClose(); },
  };

  useEffect(() => {
    if (!user) return;
    return onSnapshot(
      doc(db, 'users', user.uid, 'subscriptionData', 'planDetails'),
      snap => setPlan(snap.data() || {}),
      () => {}
    );
  }, [user]);

  const isPro = plan?.currentPlan?.toLowerCase() === 'pro';
  const subInterval = isPro ? plan.paymentInterval || null : null;
  const subMinutes = isPro ? plan.creditsLimit || null : null;
  const isCustomPlan = isPro && !subInterval;

  // Open on the user's current subscription
  useEffect(() => {
    if (subInterval) setPeriod(subInterval);
    if (subInterval && subMinutes) setMinutes(subMinutes);
  }, [subInterval, subMinutes]);

  const minuteOptions = subInterval && subMinutes && !MINUTE_OPTIONS.includes(subMinutes)
    ? [...MINUTE_OPTIONS, subMinutes].sort((a, b) => a - b)
    : MINUTE_OPTIONS;

  const isCurrent = isPro && period === subInterval && minutes === subMinutes;
  const isCancelling = isCurrent && !!plan.cancelAtPeriodEnd;
  const cycleEnd = plan?.billingCycleEnd?.toDate?.();
  const endLabel = cycleEnd ? cycleEnd.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : '';

  const startCheckout = async () => {
    setCheckout('loading');
    track('checkout_started', { source, period, minutes });
    try {
      const processCheckout = httpsCallable(getFunctions(), 'processCheckout');
      const { data: { id } } = await processCheckout({
        selectedQuantity: minutes / 1000,
        planType: 'pro',
        paymentInterval: period,
        gclid: localStorage.getItem('gclid') || '',
      });
      const stripe = await stripePromise;
      const { error } = await stripe.redirectToCheckout({ sessionId: id });
      if (error) { setCheckout('error'); track('checkout_error', { stage: 'redirect' }); }
    } catch {
      setCheckout('error');
      track('checkout_error', { stage: 'session' });
    }
  };

  let cta;
  if (!isPro) {
    cta = { label: checkout === 'loading' ? 'Loading…' : checkout === 'error' ? 'Retry' : 'Get Pro', onClick: startCheckout, disabled: checkout === 'loading' };
  } else if (isCurrent) {
    cta = { label: isCancelling ? `Your subscription will cancel on ${endLabel}` : 'Your current plan', disabled: true };
  } else {
    cta = { label: 'Update Plan', onClick: () => setShowConfirmation(true) };
  }

  const price = monthlyPrice(minutes, period);

  // Until the plan loads, only dim the page (otherwise a Pro user briefly sees "Get Pro")
  if (!plan) return <div className="plan-overlay" onClick={onClose} />;

  return (
    <div className="plan-overlay" {...backdropProps}>
      <div className="plan-container" role="dialog" aria-modal="true" aria-labelledby="plan-title">
        <button className="plan-close" onClick={onClose} aria-label="Close">
          <FontAwesomeIcon icon={faXmark} />
        </button>

        <h1 className="plan-title" id="plan-title">{isPro ? 'Your plan' : 'Upgrade your plan'}</h1>
        <p className="plan-subtitle">
          {isCustomPlan
            ? 'Your plan was set up by the Bumpups team.'
            : isPro
              ? 'Change your minutes or billing period anytime.'
              : 'Local uploads, longer videos and more minutes every month.'}
        </p>

        {!isCustomPlan && (
          <>
            <div className="plan-period-toggle">
              <button className={period === 'monthly' ? 'active' : ''} onClick={() => setPeriod('monthly')}>
                Monthly
              </button>
              <button className={period === 'yearly' ? 'active' : ''} onClick={() => setPeriod('yearly')}>
                Annual (Save 20%)
              </button>
            </div>

            <div className="plan-tier-select">
              <span className="plan-tier-label" id="plan-tier-label">Monthly minutes</span>
              <div className="plan-tier-options" role="radiogroup" aria-labelledby="plan-tier-label">
                {minuteOptions.map(value => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={minutes === value}
                    className={`plan-tier-option${minutes === value ? ' active' : ''}`}
                    onClick={() => setMinutes(value)}
                  >
                    {value.toLocaleString()} minutes
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="plan-card">
          <h2 className="plan-plan-name">{isCustomPlan ? 'Pro · Custom' : 'Pro'}</h2>

          {!isCustomPlan && (
            <div className="plan-price">
              {period === 'yearly' && (
                <span className="plan-price-old">${monthlyPrice(minutes, 'monthly')}</span>
              )}
              <span className="plan-price-dollar">$</span>
              <span className={`plan-price-amount${period === 'yearly' ? ' plan-price-yearly' : ''}`}>{price}</span>
              <span className="plan-price-unit">USD/mo</span>
            </div>
          )}

          {!isCustomPlan && (
            <p className="plan-billing">
              {period === 'yearly'
                ? `$${price * 12} billed yearly · saves $${(monthlyPrice(minutes, 'monthly') - price) * 12} a year`
                : 'Billed monthly · cancel anytime'}
            </p>
          )}

          <p className="plan-desc">
            <b>{(isCustomPlan ? subMinutes || 0 : minutes).toLocaleString()} minutes</b>
            <span className="plan-subdesc"> every month</span>
          </p>

          {isCustomPlan ? (
            <button className="plan-cta disabled" disabled>Your current plan</button>
          ) : (
            <button
              className={`plan-cta${cta.disabled ? ' disabled' : ''}${isCancelling ? ' cancelled' : ''}`}
              disabled={cta.disabled}
              onClick={cta.onClick}
            >
              {cta.label}
            </button>
          )}

          <ul className="plan-features">
            {FEATURES.map(f => <li key={f}>{f}</li>)}
          </ul>

          {!isPro ? (
            <p className="plan-footnote">
              Need more capabilities for your business?<br />
              <a href="/enterprise" target="_blank" rel="noopener noreferrer">Bumpups Enterprise</a>
            </p>
          ) : isCustomPlan ? (
            <p className="plan-footnote">
              Need a change? <a href="/help/7956855-billing">Contact billing support</a>
            </p>
          ) : !plan.cancelAtPeriodEnd ? (
            <div className="plan-footnote">
              <p><Link to="/settings/plan" onClick={onClose}>Manage my subscription</Link></p>
              <p><a href="/help/7956855-billing">I need help with a billing issue</a></p>
            </div>
          ) : null}
        </div>
      </div>

      {showConfirmation && (
        <PlanConfirmation
          total={period === 'yearly' ? price * 12 : price}
          paymentInterval={period}
          paymentMethod={plan?.paymentMethod}
          selectedQuantity={minutes / 1000}
          onClose={() => setShowConfirmation(false)}
        />
      )}
    </div>
  );
}
